'use client';
import { type PropsWithChildren, useEffect } from 'react';
import {
  useActiveAccount,
  useCurrentUser,
  useDispatch,
} from '@witmetrics/data-store';
import { UnauthorizedError } from '@witmetrics/utils/errors';
import type { Dictionary } from '@witmetrics/utils';
import { ACCOUNT_TYPES } from '@witmetrics/utils/constants';
import { useAppState } from '@/providers/AppStateProvider';
import { showErrorToaster, showWarningToaster } from '@/utils/toasters';
import { getAuthURL, getPortalURL } from '@/api/getURL';
import { isInput } from '@/utils/dom';
import { API } from '@/api';
import { DIALOGS } from '@/constants/dialogs';
import { SHORTCUT_KEYS } from '@/constants/shortcutKeys';
import { NAVIGATION_KEYS } from '@/constants/navigationKeys';
import { usePath } from '@/hooks/usePath';
import CheckingAuth from '@/components/CheckingAuth';
import DialogsManager from '@/components/DialogsManager';
import AppHeader from '@/components/AppHeader';
import AppSidebar from '@/components/AppSidebar';
import {
  classes,
  fetchPracticeData,
  getNavigation,
  getShortcut,
  isIgnoredKey,
} from './utils';

export default function Layout({ children }: PropsWithChildren) {
  const dispatch = useDispatch();
  const { onApiError, activeDialogs, setActiveDialogs } = useAppState();
  const { navigate } = usePath();
  const activeAccount = useActiveAccount();
  const currentUser = useCurrentUser();

  useEffect(() => {
    handleFetchCurrentUser();
  }, []);

  useEffect(() => {
    if (activeAccount?.portalOnly) {
      navigate(getPortalURL());
    } else if (
      currentUser &&
      // Labs/organizations don't currently have Insights access
      (!activeAccount || activeAccount.accountType !== ACCOUNT_TYPES.PRACTICE)
    ) {
      setActiveDialogs({ [DIALOGS.ACCOUNTS]: true });
    } else if (activeAccount) handleFetchAccountData();
  }, [activeAccount, currentUser]);

  useEffect(() => {
    const checkKeyPress = (e: KeyboardEvent) => {
      return handleKeyPress(e, activeDialogs);
    };
    window.addEventListener('keyup', checkKeyPress);
    return () => {
      window.removeEventListener('keyup', checkKeyPress);
    };
  }, [activeDialogs]);

  const handleFetchCurrentUser = async () => {
    try {
      const user = await API.Users.fetchCurrentUser();
      dispatch.addCurrentUser(user);
    } catch (err) {
      if (err instanceof UnauthorizedError) {
        navigate(getAuthURL(), { redirect: window.location.href });
      } else {
        showErrorToaster('Error fetching current user');
        throw err;
      }
    }
  };

  const handleFetchAccountData = async () => {
    try {
      const data = await fetchPracticeData(activeAccount!.accountId);
      dispatch.addPracticePreferences(data.practicePreferences);
      dispatch.addPracticeUsers(data.practiceUsers);
    } catch (err) {
      onApiError(err, 'Error fetching practice settings');
    }
  };

  const handleSelectShortcut = (shortcut: keyof typeof SHORTCUT_KEYS) => {
    setActiveDialogs((dialogs) => ({
      ...dialogs,
      [DIALOGS.SHORTCUTS]: false,
      [SHORTCUT_KEYS[shortcut]]: true,
    }));
  };

  const handleSelectNavigation = (shortcut: keyof typeof NAVIGATION_KEYS) => {
    setActiveDialogs((d) => ({
      ...d,
      [DIALOGS.NAVIGATION]: false,
    }));
    navigate(NAVIGATION_KEYS[shortcut]);
  };

  const handleKeyPress = (e: KeyboardEvent, dialogs: Dictionary<boolean>) => {
    if (isInput(e)) return null;
    const isShortcutsDialogShown = dialogs[DIALOGS.SHORTCUTS];
    const isNavigationDialogShown = dialogs[DIALOGS.NAVIGATION];
    if (['\\', ':'].includes(e.key) && !isNavigationDialogShown) {
      return setActiveDialogs((d) => ({ ...d, [DIALOGS.SHORTCUTS]: true }));
    }
    if (e.key === '/' && !isShortcutsDialogShown) {
      return setActiveDialogs((d) => ({ ...d, [DIALOGS.NAVIGATION]: true }));
    }
    if (isShortcutsDialogShown) {
      const shortcut = getShortcut(e);
      if (shortcut) return handleSelectShortcut(shortcut);
    } else if (isNavigationDialogShown) {
      const shortcut = getNavigation(e);
      if (shortcut) return handleSelectNavigation(shortcut);
    }
    if (isShortcutsDialogShown || isNavigationDialogShown) {
      if (isIgnoredKey(e)) return; // Ignore shift key since it's probably just being used as a modifier key
      return showWarningToaster(`Unrecognized shortcut: ${e.key}`);
    }
  };

  if (!currentUser) return <CheckingAuth />;

  return (
    <div className={classes.canvas}>
      <DialogsManager
        onSelectShortcut={handleSelectShortcut}
        onSelectNavigation={handleSelectNavigation}
      />
      <div className={classes.frame}>
        <AppHeader />
        <AppSidebar />
        <div className={classes.content}>{children}</div>
      </div>
    </div>
  );
}
